import { useContext, createContext, FC, PropsWithChildren, useState, useMemo } from 'react';
import { useToastNotification } from 'utils/hooks/useToastNotification';
import { User } from 'modules/domains/user';
import { ListFolder, ListSubfolder, useTranslation } from 'modules/domains/list';
import noop from 'utils/noop';
import { List } from '../types';
import { useEmailListsCreatorsQuery } from '../hooks';
import { useListFolders } from '../hooks/useLists/useListFolders';
import { DEFAULT_PAGINATION_INFO, GraphQLResponsePaginationInfo } from 'modules/graphql';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { useRootListFolderId } from '../hooks/folders/useRootListFolderId';
import {
  serializationOptionsForNumberValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { useFiltersContext } from './filters';
import { isEmptyString } from 'utils/stringHelpers';

export type ListsContextState = {
  folderData: ListFolder;
  folderId: number;
  isError: boolean;
  isLoading: boolean;
  listCreators?: Record<string, User>;
  listFolders: ListSubfolder[];
  lists: List[];
  listsToDelete: List[];
  setFolderId: (folderId: number) => void;
  setListsToDelete: (selectedLists: List[]) => void;
  totalSize: number;
  recursive: boolean;
  refetch: () => void;
  rootFolderId: number;
  paginationInfo: GraphQLResponsePaginationInfo;
};

export const defaultListsState = {
  folderData: {
    ancestors: [],
    content: [],
    info: DEFAULT_PAGINATION_INFO,
    id: 0,
    name: '',
    isFavorite: false,
    __typename: GRAPHQL_TYPENAMES.LIST_FOLDER,
  } as ListFolder,
  folderId: 0,
  isError: false,
  isLoading: false,
  listFolders: [],
  lists: [],
  listsToDelete: [],
  recursive: false,
  refetch: noop,
  setFolderId: noop,
  setListsToDelete: noop,
  totalSize: 0,
  rootFolderId: 0,
  paginationInfo: DEFAULT_PAGINATION_INFO,
};

export const ListsContext = createContext<ListsContextState>(defaultListsState);
export const useListsContext = () => useContext(ListsContext);

const FOLDER_ID_SEARCH_PARAM = 'folderId';

export const ListsContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { t, ready } = useTranslation();
  const { search } = useFiltersContext();
  const rootFolderId = useRootListFolderId();

  const [folderId, setFolderId] = useSearchParamState({
    searchParamKey: FOLDER_ID_SEARCH_PARAM,
    stateValueWhenSearchParamIsUnset: rootFolderId,
    ...serializationOptionsForNumberValue,
  });

  const [listsToDelete, setListsToDelete] = useState<List[]>([]);

  const {
    listsCreators,
    isError: isModifiersAndCreatorsError,
    isLoading: isModifiersAndCreatorsLoading,
  } = useEmailListsCreatorsQuery();

  // add list variables here
  const { listFolders, lists, folderData, paginationInfo, isLoading, isError, refetch } =
    useListFolders({ rootFolderId, folderId });

  useToastNotification({
    negative: isError,
    negativeMessage: t('indexPage.toasts.fetchError'),
    enabled: ready && !isLoading,
  });

  useToastNotification({
    negative: isModifiersAndCreatorsError,
    negativeMessage: t('indexPage.toasts.fetchListCreatorsError'),
    enabled: ready && !isModifiersAndCreatorsLoading,
  });

  const isRecursive = useMemo(() => !isEmptyString(search), [search]);

  const value = useMemo(
    () => ({
      folderId,
      recursive: isRecursive,
      lists,
      listFolders,
      folderData,
      listsToDelete,
      setListsToDelete,
      setFolderId,
      paginationInfo,
      isError,
      isLoading,
      totalSize: paginationInfo.count,
      refetch,
      rootFolderId,
      listsCreators,
    }),
    [
      folderId,
      lists,
      listFolders,
      folderData,
      listsToDelete,
      setListsToDelete,
      setFolderId,
      isError,
      isLoading,
      paginationInfo,
      refetch,
      rootFolderId,
      listsCreators,
      isRecursive,
    ],
  );

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
};
